import ReviewPopup from "~/components/review-popup";
import { ShowUpgradeModal } from "~/components/upgrade-funnel/showUpgradeModal";
import Categories from "~/components/ui/Categories";
import * as S from "./styles";
import { Limits } from "~/types/billing";
import Separator from "~/components/ui/Separator";
import { useState, useEffect, useMemo } from "react";
import { useGetCategories } from "~/api/dropshipping/queries";
import { SVG } from "~/components/ui/SVG";
import { useBreadcrumbItems } from "~/hooks/useBreadcrumbItems";
import { debounce } from "lodash";
import { SortByButton } from "./components/SortByButton";
import {
  getSortingOption,
  Sorting,
  SortingKey,
  sortingKeys,
  SortKey,
} from "~/constants/product-sorting";
import { faEarthAmericas, faFire } from "@fortawesome/pro-regular-svg-icons";
import Truck from "~/assets/svg/truck.svg";
import { ShipsToFilters } from "~/constants/filters";
import { useCustomSearchParams } from "~/hooks/useCustomSearchParams";
import { ProductSearchInput } from "~/components/product-search-input";
import { ProductsGrid } from "~/components/products-grid";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { ReactivateBanner } from "~/components/layout/ReactivateBanner";
import { useAccount } from "~/hooks/useAccount";
import { PlanStatus } from "~/types/account";
import { PastDueBanner } from "~/components/pastDueBanner";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import FilteredLayout from "~/pages/home/components/FilteredLayout";
import { SPOCKET_APP_ASSETS_BUCKET_URL } from "~/constants/urls";

const techAccessories = `${SPOCKET_APP_ASSETS_BUCKET_URL}/category-thumb-tech-accessories.jpg`;
const bathBeauty = `${SPOCKET_APP_ASSETS_BUCKET_URL}/category-thumb-bath-beauty.jpg`;
const pets = `${SPOCKET_APP_ASSETS_BUCKET_URL}/category-thumb-pets.jpg`;
const accessories = `${SPOCKET_APP_ASSETS_BUCKET_URL}/category-thumb-accessories.jpg`;

const HomePage = () => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const {
    values: {
      search,
      categoryId,
      searchImageId,
      fastShipping,
      shipsTo,
      bestSelling,
      shuffling,
      ...sortingParams
    },
    updateSearchParam,
    clearSearchParams,
  } = useCustomSearchParams({
    search: "string",
    categoryId: "string",
    searchImageId: "number",
    bestSelling: "boolean",
    fastShipping: "boolean",
    shipsTo: "string",
    sort: "string",
    sort_by_price: "string",
    sort_by_created_at: "string",
    sort_by_number_of_orders: "string",
    shuffling: "number",
  });

  const isPlanDisabled = useMemo(() => {
    const isPlanInactive =
      account?.last_subscription?.status === PlanStatus.INACTIVE;
    return !account?.active_subscription && isPlanInactive;
  }, [account?.last_subscription?.status, account?.active_subscription]);

  const isPastDue = useMemo(() => {
    let pastDue =
      account?.last_subscription?.status === PlanStatus.PAST_DUE &&
      !DISABLE_PAYMENTS;

    if (!!account?.stripe_card_updated_at && !!account?.last_subscription) {
      const lastCardUpdateAt = new Date(account.stripe_card_updated_at);
      const subscriptionUpdatedAt = new Date(
        account?.last_subscription?.updated_at
      );
      pastDue = pastDue && lastCardUpdateAt < subscriptionUpdatedAt;
    }

    return pastDue;
  }, [account]);

  const sorting = useMemo(
    () => getSortingOption((key) => sortingParams[key] ?? null),
    [sortingParams]
  );

  const [width, setWidth] = useState(0);

  useEffect(() => {
    setWidth(window.innerWidth);
    const handleResize = debounce(() => setWidth(window.innerWidth), 50);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const updateSorting = (newSorting: Sorting) => {
    if (newSorting === null) {
      sortingKeys.forEach((key) => updateSearchParam(key, null));
      return;
    }

    const newKey = Object.keys(newSorting)[0] as SortingKey;

    sortingKeys.forEach((key) => {
      if (key === newKey) {
        updateSearchParam(key, newSorting[newKey] ?? null);
        return;
      }

      updateSearchParam(key, null);
    });
  };

  const { data: categories } = useGetCategories();
  const { topLevelCategories, dropdownCategories } = useMemo(() => {
    const activeCategories = categories?.filter(
      (c) => c.parent === null && c.is_active
    );

    const topLevelCategories = activeCategories?.filter((c) => c.is_visible);
    const dropdownCategories = activeCategories?.filter((c) => !c.is_visible);

    if (topLevelCategories) {
      const imageMap = {
        "Tech Accessories": techAccessories,
        "Bath & Beauty": bathBeauty,
        Pets: pets,
        Accessories: accessories,
      };

      topLevelCategories.forEach((category) => {
        if (!category.image) {
          category.image =
            imageMap[category.name as keyof typeof imageMap] || "";
        }
      });
    }
    return { topLevelCategories, dropdownCategories };
  }, [categories]);

  const selectedCategory = categories?.find(
    (c) => c.external_id === categoryId
  );
  const hasFilters = !!search || !!selectedCategory || !!searchImageId;

  const isFilterLayout =
    !!fastShipping ||
    !!shipsTo ||
    !!bestSelling ||
    !sortingParams ||
    (sortingParams && sortingParams.sort);

  const breadcrumbItems = useBreadcrumbItems({
    baseItems: [
      {
        translation: t("dropshipping.search_breadcrumb"),
        onClick: hasFilters
          ? () => {
              clearSearchParams();
            }
          : undefined,
      },
    ],
    searchParamItems: {
      search: {
        translation: (value) =>
          t("dropshipping.search_with_term", { term: value }),
      },
      categoryId: {
        translation: selectedCategory?.name ?? "",
      },
      searchImageId: {
        translation: t("dropshipping.image_you_uploaded"),
      },
    },
  });

  const getRandomCategories = useMemo(() => {
    if (categoryId) return categoryId;
    if (!categories) return undefined;

    const randomCategories = [...categories];
    const categoriesIds = randomCategories
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((c) => c.external_id);
    return categoriesIds.join(",");
  }, [categoryId, categories]);

  return (
    <S.Container>
      {isPastDue ? <PastDueBanner /> : null}
      {isPlanDisabled && !DISABLE_PAYMENTS ? (
        <ReactivateBanner account={account} />
      ) : null}

      <ReviewPopup />
      <ProductSearchInput
        placeholder={t("dropshipping.search_aliexpress")}
        disableImageSearch
      />

      <Separator type="horizontal" className="search__divider" />

      <Categories
        rows={1}
        columns={6}
        topLevelCategories={topLevelCategories ?? []}
        dropdownCategories={dropdownCategories}
        fillRow={topLevelCategories?.[0]?.name}
        onClick={(category) =>
          updateSearchParam("categoryId", category.external_id)
        }
      />

      <S.FiltersContainer>
        <S.BooleanFilterButton
          active={bestSelling}
          onClick={() => {
            updateSearchParam("bestSelling", (prev) => !prev);
            updateSearchParam("sort", "LAST_VOLUME_DESC");
          }}>
          <SVG icon={faFire} color="red" />
          {t("dropshipping.best_selling")}
        </S.BooleanFilterButton>

        <S.BooleanFilterButton
          active={fastShipping}
          onClick={() => updateSearchParam("fastShipping", (prev) => !prev)}>
          <img src={Truck} alt="Truck" width={20} height={20} />
          {t("dropshipping.fast_shipping")}
        </S.BooleanFilterButton>

        <S.StyledFilter
          filters={ShipsToFilters}
          onChange={(countries) => {
            updateSearchParam("shipsTo", shipsTo ? countries[1] : countries[0]);
            return true;
          }}
          value={shipsTo ? [shipsTo] : []}
          active={!!shipsTo?.length}
          icon={faEarthAmericas}
          iconColor="green"
          label={t("dropshipping.ships_to")}
        />

        {width > 768 ? (
          <SortByButton sorting={sorting} updateSorting={updateSorting} />
        ) : null}
      </S.FiltersContainer>
      {width <= 768 ? (
        <SortByButton
          sorting={sorting}
          updateSorting={updateSorting}
          isOnSearchPage
        />
      ) : null}

      {isFilterLayout && (
        <FilteredLayout
          clear={(key) => updateSearchParam(key, () => null)}
          clearAll={() => clearSearchParams()}
          fastShipping={fastShipping}
          shipsTo={shipsTo}
          bestSelling={bestSelling}
          sortingParams={sortingParams?.sort}
        />
      )}
      <Separator type="horizontal" className="search__divider" />

      {hasFilters && <S.StyledBreadcrumbs items={breadcrumbItems} />}

      <ProductsGrid
        filters={{
          search_term: search,
          search_image_id: searchImageId ?? undefined,
          category: search ? categoryId : getRandomCategories,
          ships_to: shipsTo ?? undefined,
          fast_shipping: fastShipping,
          shuffling: shuffling,
          sort: (sortingParams.sort as SortKey) ?? undefined,
          ...sorting,
        }}
        requiredLimit={Limits.PAID_PLAN}
      />
      <ShowUpgradeModal />
    </S.Container>
  );
};

export default HomePage;
